import {
  FloorplanDecorKind,
  FloorplanLightingPreset,
  FloorplanLengthUnit,
  FloorplanObjectType,
  FloorplanRoomShellSourceType,
  FloorplanViewAngle,
} from '../constants/venueLayout'
import type { FloorplanDocument } from '../venueLayout'
import {
  createEmptyFloorplanDocument,
  floorplanDocumentSchema,
} from '../schemas/venueLayout'

export const FloorplanTemplateId = {
  BLANK: 'blank',
  SMALL_LOUNGE: 'small-lounge',
} as const

export type FloorplanTemplateId =
  (typeof FloorplanTemplateId)[keyof typeof FloorplanTemplateId]

export type FloorplanTemplateDefinition = {
  id: FloorplanTemplateId
  name: string
  description: string
  document: FloorplanDocument
}

type FloorplanTemplateMetadata = Omit<FloorplanTemplateDefinition, 'document'>

export const DEFAULT_FLOORPLAN_TEMPLATE_ID: FloorplanTemplateId =
  FloorplanTemplateId.BLANK

function cloneFloorplanDocument(
  document: FloorplanDocument
): FloorplanDocument {
  return floorplanDocumentSchema.parse(JSON.parse(JSON.stringify(document)))
}

const smallLoungeTemplate = floorplanDocumentSchema.parse({
  shell: {
    unit: FloorplanLengthUnit.FT,
    rooms: [
      {
        id: 'room_main',
        name: 'Main Lounge',
        x: 80,
        y: 80,
        polygon: [0, 0, 680, 0, 680, 440, 0, 440],
        walls: [
          {
            id: 'room_main_wall_1',
            kind: 'wall',
            points: [0, 0, 680, 0],
            thicknessCm: 2,
            heightCm: 0,
            wallColor: null,
          },
          {
            id: 'room_main_wall_2',
            kind: 'wall',
            points: [680, 0, 680, 440],
            thicknessCm: 2,
            heightCm: 0,
            wallColor: null,
          },
          {
            id: 'room_main_wall_3',
            kind: 'wall',
            points: [680, 440, 0, 440],
            thicknessCm: 2,
            heightCm: 0,
            wallColor: null,
          },
          {
            id: 'room_main_wall_4',
            kind: 'wall',
            points: [0, 440, 0, 0],
            thicknessCm: 2,
            heightCm: 0,
            wallColor: null,
          },
        ],
        partitions: [
          {
            id: 'room_main_partition_bar',
            kind: 'partition',
            points: [180, 100, 440, 100],
            thicknessCm: 6,
            heightCm: 10,
            wallColor: null,
          },
          {
            id: 'room_main_partition_vip_divider',
            kind: 'partition',
            points: [460, 100, 460, 420],
            thicknessCm: 4,
            heightCm: 8,
            wallColor: null,
          },
          {
            id: 'room_main_partition_stage',
            kind: 'partition',
            points: [60, 360, 260, 360],
            thicknessCm: 5,
            heightCm: 10,
            wallColor: null,
          },
        ],
        openings: [],
      },
      {
        id: 'room_entry',
        name: 'Entry',
        x: 80,
        y: 80,
        polygon: [0, 0, 180, 0, 180, 100, 0, 100],
        walls: [
          {
            id: 'room_entry_wall_1',
            kind: 'wall',
            points: [0, 0, 180, 0],
            thicknessCm: 2,
            heightCm: 0,
            wallColor: null,
          },
          {
            id: 'room_entry_wall_2',
            kind: 'wall',
            points: [180, 0, 180, 100],
            thicknessCm: 2,
            heightCm: 0,
            wallColor: null,
          },
          {
            id: 'room_entry_wall_3',
            kind: 'wall',
            points: [180, 100, 0, 100],
            thicknessCm: 2,
            heightCm: 0,
            wallColor: null,
          },
          {
            id: 'room_entry_wall_4',
            kind: 'wall',
            points: [0, 100, 0, 0],
            thicknessCm: 2,
            heightCm: 0,
            wallColor: null,
          },
        ],
        partitions: [],
        openings: [],
      },
    ],
    fixtures: [],
    source: { type: FloorplanRoomShellSourceType.MANUAL },
  },
  objects: [
    {
      id: 'section_main',
      type: FloorplanObjectType.SECTION,
      venueLayoutSectionId: 'template-main',
      polygon: [120, 210, 520, 210, 520, 500, 120, 500],
      floorColor: '#2e1f1b',
      heightCm: 0,
    },
    {
      id: 'section_vip',
      type: FloorplanObjectType.SECTION,
      venueLayoutSectionId: 'template-vip',
      polygon: [560, 210, 720, 210, 720, 500, 560, 500],
      floorColor: '#1f1f34',
      heightCm: 1,
    },
    {
      id: 'section_bar',
      type: FloorplanObjectType.SECTION,
      venueLayoutSectionId: 'template-bar',
      polygon: [120, 110, 520, 110, 520, 175, 120, 175],
      floorColor: '#3f2d1f',
      heightCm: 1,
    },
    {
      id: 'label_main',
      type: FloorplanObjectType.LABEL,
      text: 'Main Lounge',
      x: 305,
      y: 350,
      rotation: 0,
    },
    {
      id: 'label_vip',
      type: FloorplanObjectType.LABEL,
      text: 'VIP',
      x: 640,
      y: 340,
      rotation: 0,
    },
    {
      id: 'label_bar',
      type: FloorplanObjectType.LABEL,
      text: 'Craft Bar',
      x: 320,
      y: 138,
      rotation: 0,
    },
    {
      id: 'decor_dj',
      type: FloorplanObjectType.DECOR,
      decorKind: FloorplanDecorKind.DJ_BOOTH,
      x: 180,
      y: 470,
      widthCm: 120,
      heightCm: 46,
      rotation: 0,
    },
    {
      id: 'decor_plant_1',
      type: FloorplanObjectType.DECOR,
      decorKind: FloorplanDecorKind.PLANT,
      x: 120,
      y: 500,
      widthCm: 26,
      heightCm: 26,
      rotation: 0,
    },
    {
      id: 'decor_plant_2',
      type: FloorplanObjectType.DECOR,
      decorKind: FloorplanDecorKind.PLANT,
      x: 730,
      y: 500,
      widthCm: 26,
      heightCm: 26,
      rotation: 0,
    },
    {
      id: 'decor_art',
      type: FloorplanObjectType.DECOR,
      decorKind: FloorplanDecorKind.ART,
      x: 642,
      y: 132,
      widthCm: 64,
      heightCm: 28,
      rotation: 0,
    },
    {
      id: 'decor_rope',
      type: FloorplanObjectType.DECOR,
      decorKind: FloorplanDecorKind.ROPE,
      x: 535,
      y: 240,
      widthCm: 10,
      heightCm: 220,
      rotation: 0,
    },
    {
      id: 'lighting_main',
      type: FloorplanObjectType.LIGHTING_ZONE,
      polygon: [120, 210, 520, 210, 520, 500, 120, 500],
      preset: FloorplanLightingPreset.WARM,
      intensity: 0.52,
      color: '#f59e0b',
    },
    {
      id: 'lighting_vip',
      type: FloorplanObjectType.LIGHTING_ZONE,
      polygon: [560, 210, 720, 210, 720, 500, 560, 500],
      preset: FloorplanLightingPreset.VIP,
      intensity: 0.46,
      color: '#8b5cf6',
    },
  ],
  theme: {
    backgroundColor: '#141016',
    floorTexture: 'dark-wood',
    lightingPresetId: 'lounge-warm',
    viewAngle: FloorplanViewAngle.TOP,
  },
  traceBackground: null,
  calibration: null,
})

const floorplanTemplates: Record<
  FloorplanTemplateId,
  FloorplanTemplateDefinition
> = {
  [FloorplanTemplateId.BLANK]: {
    id: FloorplanTemplateId.BLANK,
    name: 'Blank canvas',
    description: 'Start from scratch with an empty floorplan.',
    document: createEmptyFloorplanDocument(),
  },
  [FloorplanTemplateId.SMALL_LOUNGE]: {
    id: FloorplanTemplateId.SMALL_LOUNGE,
    name: 'Small Lounge',
    description:
      'A compact lounge with a bar edge, VIP zone, ambient lighting, and decor.',
    document: smallLoungeTemplate,
  },
}

export function listFloorplanTemplateMetadata(): FloorplanTemplateMetadata[] {
  return Object.values(floorplanTemplates).map(
    ({ id, name, description }): FloorplanTemplateMetadata => ({
      id,
      name,
      description,
    })
  )
}

export function getFloorplanTemplateById(
  templateId: FloorplanTemplateId
): FloorplanTemplateDefinition {
  return floorplanTemplates[templateId]
}

export function buildFloorplanTemplateDocument(
  templateId: FloorplanTemplateId
): FloorplanDocument {
  return cloneFloorplanDocument(getFloorplanTemplateById(templateId).document)
}
